"use client";

import { useState } from "react";
import { useLanguage } from "../../contexts/LanguageContext";
import { Band, SectionHead } from "../ui/Section";
import Reveal from "../ui/Reveal";
import Icon from "../ui/Icon";

const MANAGEMENT_URL = "https://management.cosebu.com/";
const SALES_EMAIL = "mailto:hyphentechinfo@gmail.com";

/** "40,000 FBU/month" -> { amount: "40,000 FBU", period: "month" } */
const splitPrice = (price = "") => {
  const [amount, period] = price.split("/");
  return { amount: amount.trim(), period: period ? period.trim() : null };
};

const FeatureList = ({ features, limit, expanded, onToggle, t }) => {
  const visible = expanded ? features : features.slice(0, limit);
  return (
    <>
      <ul className="space-y-3">
        {visible.map((feature) => (
          <li key={feature} className="flex gap-3">
            <Icon
              name="check"
              size={15}
              strokeWidth={2}
              className="mt-[3px] flex-none text-accent"
            />
            <span className="text-[14px] leading-relaxed text-ink-2">{feature}</span>
          </li>
        ))}
      </ul>

      {features.length > limit ? (
        <button
          type="button"
          onClick={onToggle}
          aria-expanded={expanded}
          className="link mt-4 text-[13px]"
        >
          {expanded ? t.pricing.readLess : t.pricing.readMore}
          <Icon
            name="arrowUp"
            size={13}
            className={`transition-transform duration-200 ${expanded ? "" : "rotate-180"}`}
          />
        </button>
      ) : null}
    </>
  );
};

const PlanCard = ({ plan, featured, expanded, onToggle, t }) => {
  const { amount, period } = splitPrice(plan.price);
  const unavailable = plan.comingSoon;

  return (
    <Reveal
      className={[
        "panel relative flex flex-col p-7 md:p-8",
        featured ? "border-accent" : "",
      ].join(" ")}
    >
      {featured ? (
        <span className="absolute -top-3 left-7 bg-surface px-2">
          <span className="tag tag--accent">{t.pricing.popular}</span>
        </span>
      ) : null}

      <h3 className="text-[17px] font-semibold tracking-[-0.01em]">{plan.name}</h3>

      <p className="mt-5 flex items-baseline gap-1.5">
        <span className="num text-[30px] leading-none">{amount}</span>
        {period ? <span className="meta text-[13px]">/{period}</span> : null}
      </p>

      <div className="mt-7 border-t border-line pt-6">
        <p className="eyebrow mb-4">{t.pricing.allFeatures}</p>
        <FeatureList
          features={plan.features}
          limit={5}
          expanded={expanded}
          onToggle={onToggle}
          t={t}
        />
      </div>

      <div className="mt-8 pt-2">
        {unavailable ? (
          <span className="btn btn--secondary btn--block" aria-disabled="true">
            {t.pricing.comingSoon}
          </span>
        ) : (
          <a
            href={plan.link}
            target={plan.link?.startsWith("mailto:") ? undefined : "_blank"}
            rel="noopener noreferrer"
            className={`btn btn--block ${featured ? "btn--primary" : "btn--secondary"}`}
          >
            {plan.cta}
            <Icon name="arrowUpRight" size={15} />
          </a>
        )}
      </div>
    </Reveal>
  );
};

const Pricing = () => {
  const { t } = useLanguage();
  const [expanded, setExpanded] = useState({});

  const toggle = (key) =>
    setExpanded((previous) => ({ ...previous, [key]: !previous[key] }));

  const plans = t.pricing.plans;

  const businessPlans = [
    { ...plans.management, key: "management", link: MANAGEMENT_URL, cta: t.pricing.tryNow },
    {
      ...plans.managementAI,
      key: "managementAI",
      link: MANAGEMENT_URL,
      cta: t.pricing.tryNow,
      featured: true,
    },
    {
      ...plans.customSolution,
      key: "custom",
      link: SALES_EMAIL,
      cta: t.pricing.contactUs,
    },
  ];

  const marketplacePlans = [
    { ...plans.marketplaceUsers, key: "users", free: true },
    { ...plans.marketplaceBrokers, key: "brokers", comingSoon: true },
  ];

  return (
    <Band id="pricing" tint>
      <SectionHead index={5} label={t.pricing.eyebrow} title={t.pricing.title}>
        {t.pricing.subtitle}
      </SectionHead>

      <div className="rail">
        <div className="rail__label" aria-hidden="true" />

        <div>
          <Reveal className="panel mb-10 flex items-start gap-3 border-dashed p-4">
            <Icon name="spark" size={17} className="mt-0.5 flex-none text-clay" />
            <p className="meta text-[13px]">{t.pricing.freeTrialOffer}</p>
          </Reveal>

          <div className="grid gap-6 lg:grid-cols-3">
            {businessPlans.map((plan) => (
              <PlanCard
                key={plan.key}
                plan={plan}
                featured={Boolean(plan.featured)}
                expanded={Boolean(expanded[plan.key])}
                onToggle={() => toggle(plan.key)}
                t={t}
              />
            ))}
          </div>

          {/* Marketplace tiers are not a purchase decision — they read as rows. */}
          <div className="mt-14">
            <Reveal>
              <h3 className="title-sm">{t.marketplace.title}</h3>
            </Reveal>

            <div className="mt-6 divide-rule border-y border-line">
              {marketplacePlans.map((plan) => (
                <Reveal key={plan.key} className="grid gap-5 py-7 md:grid-cols-12 md:gap-8">
                  <div className="md:col-span-4">
                    <h4 className="text-[15px] font-semibold">{plan.name}</h4>
                    <p className="num mt-2 text-[20px] leading-none">
                      {plan.free ? t.pricing.free : plan.price}
                    </p>
                    {plan.comingSoon ? (
                      <span className="tag tag--next mt-3">{t.pricing.comingSoon}</span>
                    ) : (
                      <span className="tag tag--live mt-3">
                        <span className="dot" />
                        {t.pricing.free}
                      </span>
                    )}
                  </div>

                  <div className="md:col-span-8">
                    <FeatureList
                      features={plan.features}
                      limit={3}
                      expanded={Boolean(expanded[plan.key])}
                      onToggle={() => toggle(plan.key)}
                      t={t}
                    />
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </div>
    </Band>
  );
};

export default Pricing;
